import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Layout from "../../Layout/Layout";
import { ProgressSpinner } from "primereact/progressspinner";
import { Skeleton } from "primereact/skeleton";
import { Button } from "../../../components/ui/Button";
import { InputText } from "primereact/inputtext";

import ScheduleCard from "./Component/ScheduleCard";
import MatchSchedulerDialog from "./Component/MatchSchedulerDialog";
import FeatureLocked from "../../SharedComponents/FeatureLocked";

import {
    usePopularLeagues,
    useSearchLeagues,
    useFixturesByLeague,
    useGetMatchSchedules,
    useDeleteMatchSchedule,
} from "../../../queries/matchScheduler";

/**
 * Match Scheduler — UX-audit redesign.
 *
 *   1. League chip selector at the top (popular leagues + search).
 *   2. Upcoming-fixture cards in the middle ("Schedule This Match" CTA).
 *   3. Live matches row when something's currently being played.
 *   4. "My Scheduled Matches" grid below.
 *
 * The "Schedule This Match" button opens MatchSchedulerDialog with the
 * fixture pre-selected. The dialog walks the user through 2 simple steps
 * (campaign + screens) and POSTs to /api/match-scheduler/create.
 *
 * All API hooks are existing — no backend touches.
 */

// Friendly emojis for the popular-league chips. Server returns leagues as
// { id, name, logo, country, season }. We pick an emoji from the country
// code when present, falling back to the trophy.
const FLAGS = {
    SA: '🇸🇦', GB: '🏴', "GB-ENG": '🏴', ENG: '🏴',
    ES: '🇪🇸', DE: '🇩🇪', IT: '🇮🇹', FR: '🇫🇷', PT: '🇵🇹',
    NL: '🇳🇱', BR: '🇧🇷', AR: '🇦🇷', QA: '🇶🇦', AE: '🇦🇪',
    EG: '🇪🇬', JO: '🇯🇴', LB: '🇱🇧',
};

function leagueEmoji(l) {
    const cc = (l?.country_code || l?.country || '').toString().toUpperCase().slice(0, 2);
    if (FLAGS[cc]) return FLAGS[cc];
    if ((l?.name || '').toLowerCase().includes('champions')) return '🏆';
    if ((l?.name || '').toLowerCase().includes('europa'))    return '🏆';
    return '🏆';
}

function FixtureCard({ fixture, onSchedule }) {
    const { t } = useTranslation();
    const kickoff = fixture?.kickoff_at ? new Date(fixture.kickoff_at) : null;
    const dateStr = kickoff?.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    const timeStr = kickoff?.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    const live = ['1H', 'HT', '2H', 'ET', 'BT', 'P'].includes(fixture?.status);

    return (
        <div className={`pp-card p-3 d-flex flex-column gap-2 h-100 ${live ? 'pp-card-live' : ''}`}>
            <div className="d-flex align-items-center gap-2 small text-muted">
                {fixture.league_logo && (
                    <img src={fixture.league_logo} alt="" style={{ width: 16, height: 16, objectFit: 'contain' }}
                         onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                )}
                <span className="text-truncate">{fixture.league_name ?? ''}</span>
                {live && <span className="pp-status pp-status-danger ms-auto">{t('match_scheduler.live')}</span>}
            </div>

            <div className="d-flex align-items-center justify-content-between my-1">
                <div className="d-flex align-items-center gap-2 flex-grow-1 min-w-0">
                    {fixture.home_logo && (
                        <img src={fixture.home_logo} alt="" style={{ width: 28, height: 28, objectFit: 'contain', flexShrink: 0 }}
                             onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    )}
                    <span className="fw-semibold text-truncate">{fixture.home_team}</span>
                </div>
                <span className="mx-2 small text-muted">vs</span>
                <div className="d-flex align-items-center gap-2 flex-grow-1 min-w-0 justify-content-end">
                    <span className="fw-semibold text-truncate text-end">{fixture.away_team}</span>
                    {fixture.away_logo && (
                        <img src={fixture.away_logo} alt="" style={{ width: 28, height: 28, objectFit: 'contain', flexShrink: 0 }}
                             onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    )}
                </div>
            </div>

            <div className="d-flex align-items-center gap-3 small text-muted">
                <span><i className="pi pi-calendar me-1" />{dateStr}</span>
                <span><i className="pi pi-clock me-1" />{timeStr}</span>
            </div>

            <Button
                icon="pi pi-calendar-plus"
                label={t('match_scheduler.schedule_this')}
                onClick={() => onSchedule(fixture)}
                className="mt-auto"
            />
        </div>
    );
}

export default function MatchSchedulerIndex() {
    const { t } = useTranslation();

    const [selectedLeague, setSelectedLeague] = useState(null);
    const [leagueSearch,   setLeagueSearch]   = useState('');
    const [pickedFixture,  setPickedFixture]  = useState(null);
    const [browseOpen,     setBrowseOpen]     = useState(false);

    // Curated league list from API-Football, cached 24h server-side. Un-gated,
    // so it renders for non-paying venues too — they meet the upsell when they
    // try to schedule, not when they browse.
    const { data: popularLeaguesData, isLoading: loadingPop } = usePopularLeagues();
    const { data: searchedLeagues }                          = useSearchLeagues(
        leagueSearch.length >= 2 ? leagueSearch : ''
    );

    // Landing fixture list. No season is passed: the backend resolves the
    // league's CURRENT season, which is what makes this correct across a season
    // rollover without any client-side date arithmetic.
    const { data: fixturesData, isLoading: loadingUp } = useFixturesByLeague(selectedLeague?.id);

    const { data: schedulesData, isLoading: loadingSchedules, error: schedulesError } = useGetMatchSchedules();
    const deleteMutation  = useDeleteMatchSchedule();
    const schedules = Array.isArray(schedulesData) ? schedulesData : schedulesData?.data ?? [];

    // Backend may wrap the payload in { data: [...] } via CompanyResource.
    const popularLeagues = useMemo(() => {
        const arr = Array.isArray(popularLeaguesData) ? popularLeaguesData
                  : Array.isArray(popularLeaguesData?.data) ? popularLeaguesData.data : [];
        return arr;
    }, [popularLeaguesData]);

    // Auto-pick the first popular league for an instant fixture list.
    useEffect(() => {
        if (!selectedLeague && popularLeagues.length > 0) {
            setSelectedLeague(popularLeagues[0]);
        }
    }, [popularLeagues, selectedLeague]);

    const leaguesToShow = leagueSearch.length >= 2
        ? (Array.isArray(searchedLeagues) ? searchedLeagues : (searchedLeagues?.data ?? []))
        : popularLeagues;

    const fixtureRows = Array.isArray(fixturesData) ? fixturesData : (fixturesData?.data ?? []);

    if (schedulesError?.response?.status === 402) {
        return (
            <Layout>
                <FeatureLocked feature="match_scheduler" message={schedulesError.response.data?.message} />
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="container-fluid py-3" style={{ maxWidth: 1200 }}>
                <h2 className="pp-page-title mb-1"><i className="pi pi-flag-fill" /> {t('match_scheduler.title')}</h2>
                <p className="text-muted mb-4">{t('match_scheduler.subtitle')}</p>

                {/* ── Section 1: League chips ─────────────────────────── */}
                <div className="pp-card p-3 mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                        <strong className="small text-uppercase" style={{ color: 'var(--pp-text-secondary)', letterSpacing: '0.04em' }}>
                            <i className="pi pi-trophy me-1" style={{ color: 'var(--pp-primary)' }} />
                            {t('match_scheduler.league')}
                        </strong>
                        {/* Let .p-input-icon-left do its own job. It used to carry
                            `top: 13px` inline, which replaced primereact.css's
                            `top: 50%` but NOT the `margin-top: -.5rem` that exists
                            only to re-centre it — so the icon sat 5px from the top
                            of the box, riding above the placeholder. The class
                            already supplies position:relative, `left: 0.75rem` on
                            the icon and `padding-left: 2.5rem` on the input
                            (lara-light-indigo theme.css:1108-1117), so all three of
                            those inline styles were doing harm. Width stays — that
                            one was sizing, not fighting. */}
                        <span className="p-input-icon-left" style={{ minWidth: 220 }}>
                            <i className="pi pi-search" style={{ color: 'var(--pp-text-secondary)' }} />
                            <InputText
                                value={leagueSearch}
                                onChange={(e) => setLeagueSearch(e.target.value)}
                                placeholder={t('match_scheduler.search_league')}
                                style={{ width: '100%' }}
                            />
                        </span>
                    </div>
                    {loadingPop ? (
                        <div className="d-flex gap-2"><Skeleton width="120px" height="36px" /><Skeleton width="100px" height="36px" /><Skeleton width="140px" height="36px" /></div>
                    ) : leaguesToShow.length === 0 ? (
                        <p className="text-muted small mb-0">{t('match_scheduler.no_leagues')}</p>
                    ) : (
                        <div className="pp-chip-bar-inner" style={{ paddingBottom: 4 }}>
                            {leaguesToShow.map((l) => {
                                const active = selectedLeague?.id === l.id;
                                return (
                                    <button
                                        key={l.id}
                                        className={`pp-chip ${active ? 'pp-chip-active' : ''}`}
                                        onClick={() => setSelectedLeague(l)}
                                        style={{ minHeight: 38 }}
                                    >
                                        {l.logo
                                            ? <img src={l.logo} alt="" style={{ width: 16, height: 16, objectFit: 'contain' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                            : <span>{leagueEmoji(l)}</span>}
                                        {l.name}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Live-now row removed for page-load — it required the
                    API-Football key and the 503 toast scared users on first
                    visit. Live status is on-demand via the schedule's status
                    badge once a fixture is actually scheduled. */}

                {/* ── Section 2: Upcoming fixtures grid ───────────────── */}
                <div className="mb-4">
                    <h5 className="mb-2">
                        <i className="pi pi-calendar me-2" style={{ color: 'var(--pp-primary)' }} />
                        {t('match_scheduler.upcoming')}
                        {selectedLeague && <span className="text-muted ms-2 small">— {selectedLeague.name}</span>}
                    </h5>
                    {!selectedLeague ? (
                        <p className="text-muted small">{t('match_scheduler.pick_a_league')}</p>
                    ) : loadingUp ? (
                        <div className="d-flex justify-content-center py-5"><ProgressSpinner style={{ width: 40, height: 40 }} /></div>
                    ) : fixtureRows.length === 0 ? (
                        <p className="text-muted small">{t('match_scheduler.no_fixtures')}</p>
                    ) : (
                        <div className="row g-3">
                            {fixtureRows.slice(0, 12).map((fx) => (
                                <div key={fx.fixture_id} className="col-12 col-md-6 col-xl-4">
                                    <FixtureCard fixture={fx} onSchedule={setPickedFixture} />
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* ── Section 3: My scheduled matches ─────────────────── */}
                <div className="pp-card p-3">
                    {/*
                      * "+ Create" is the fixture-browser entry point: schedule a
                      * match that is not in the league rows above (a different
                      * competition, or further out than the list reaches).
                      * "Schedule This Match" on a fixture card remains the fast
                      * path and skips the browser.
                      */}
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0">
                            <i className="pi pi-list me-2" style={{ color: 'var(--pp-primary)' }} />
                            {t('match_scheduler.my_scheduled')}
                            <span className="pp-status pp-status-neutral ms-2">{schedules.length}</span>
                        </h5>
                        <Button
                            icon="pi pi-plus"
                            label={t('match_scheduler.new_schedule')}
                            className="p-button-sm"
                            onClick={() => setBrowseOpen(true)}
                        />
                    </div>
                    {loadingSchedules ? (
                        <div className="d-flex justify-content-center py-4"><ProgressSpinner style={{ width: 32, height: 32 }} /></div>
                    ) : schedules.length === 0 ? (
                        <p className="text-muted small mb-0">{t('match_scheduler.no_schedules')}</p>
                    ) : (
                        <div className="row g-3">
                            {schedules.map((s) => (
                                <div key={s.id} className="col-12 col-md-6 col-xl-4">
                                    <ScheduleCard
                                        schedule={s}
                                        onDelete={(id) => deleteMutation.mutate(id)}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/*
              * One dialog, two entry points. `pickedFixture` set => the caller
              * already chose a fixture (Schedule This Match) and the dialog
              * opens straight on the configure step; `browseOpen` => the
              * fixture browser runs first (+ Create).
              */}
            <MatchSchedulerDialog
                open={!!pickedFixture || browseOpen}
                fixture={pickedFixture}
                onClose={() => { setPickedFixture(null); setBrowseOpen(false); }}
            />
        </Layout>
    );
}
